// Dictionnaires de traduction de l'interface (hors phrases aléatoires, voir data/comments.js).
// Clés pointées (ex. "profile.title"), avec {paramètres} substitués par core/i18n.js#translate.
export const translations = Object.freeze({
  fr: {
    // Pieds de page : « ZERO KAMI v{version} · un jeu 0cog™ » (brand.line) en bas de tous les écrans ; l'écran d'accueil ajoute licence et marque
    legal: {
      product: 'ZERO KAMI v{version}',
      licensed: 'Édité et distribué par 0cog Inc. sous licence exclusive.',
      trademark: '0cog™ est une marque de 0cog Inc.',
    },
    // Fenêtre « À propos » (lien de l'écran d'accueil) : la page de crédits de l'accord de licence exclusive, art. 6.2
    about: {
      link: 'À propos',
      title: 'À propos',
      contributors: 'Contributeurs',
      thanks: 'Remerciements',
      thanksLead: 'Merci aux auteurs des logiciels et ressources libres utilisés dans ZERO KAMI :',
      sources: 'Sources',
      rights: '© 2026 contributeurs de ZERO KAMI. Tous droits réservés.',
      // Lien vers la politique de confidentialité, demandé dans l'appli par l'App Store (règle 5.1.1(i)) et Google Play
      privacy: 'Politique de confidentialité',
    },
    meta: {
      lang: 'fr',
      description: 'ZERO KAMI — photographie toute la famille de fantômes avant la fin de la pellicule.',
    },
    app: { subtitle: 'Les Kamis sont là, ne les rate pas', level: 'Niveau {level}' },
    // Ligne de marque (voir zerocog/zero-kami#6) : toujours ™ tant que la marque n'est pas enregistrée
    brand: { line: 'un jeu 0cog™' },
    settings: {
      title: 'Réglages',
      language: 'Langue',
      replayTips: 'Revoir les astuces',
      replayNote: "Affichées à l'enregistrement",
      reducedMotion: 'Réduire les animations',
      iosVersion: 'Version iOS (simulation)',
      iosNote: "Pas encore disponible : une charge rapide offerte par niveau",
      resetGame: 'Réinitialiser la partie',
      resetNote: "Efface la galerie, les récompenses et le niveau à l'enregistrement",
      game: 'Réglages de jeu',
      tabs: 'Catégories de réglages',
      tabGeneral: 'Général',
      gameNote: 'Enregistrés sur cet appareil seulement : les autres joueurs gardent les réglages par défaut.',
      resetSettings: '↺ Remettre les réglages par défaut',
      resetDone: 'Réglages remis par défaut : enregistre pour les garder',
      save: 'Enregistrer',
      cancel: 'Annuler',
      unsaved: 'Modifications non enregistrées',
      saved: 'Réglages enregistrés',
    },
    help: {
      title: 'Astuces',
      step: 'Étape {n} sur {total}',
      swipe: "Fais glisser pour l'étape suivante",
      continue: 'Continuer',
      letsGo: "C'est parti !",
      s1: { title: 'Les kamis sont là', text: 'Des petits esprits se cachent partout autour de toi. Trouve-les tous et crée ton propre album.', reward: "À chaque niveau, de nouveaux kamis t'attendent." },
      s2: { title: 'Glisse pour explorer', text: 'Fais glisser le décor du doigt. Les coins du cadre clignotent du côté où se cache un kami.', reward: 'Sur ton téléphone, en mode VR, les kamis sont tout autour de toi.' },
      s3: { title: 'Cadre et déclenche', text: 'Place un kami dans le cercle : le bouton devient vert. Appuie dessus.', reward: 'Plus tu photographies de kamis, plus la victoire est proche.' },
      s4: { title: 'Surveille ta batterie', text: "Elle se vide avec chaque photo, et avec le temps. Quand elle est vide, l'appareil se recharge : il faut patienter.", reward: 'Partage un kami pour une charge rapide et reprendre la partie sans attendre.' },
      s5: { title: 'Tourne ton téléphone', text: 'Couche ton téléphone pour jouer en mode panoramique : un grand cadre pour traquer les kamis.', reward: "Pour cela, installe d'abord l'appli sur ton écran d'accueil." },
    },
    splash: { subtitle: 'Les Kamis sont là, ne les rate pas' },
    install: {
      launchPopup: {
        message: "Installer le jeu sur l'écran d'accueil",
        yes: 'Oui',
        later: 'Plus tard',
      },
      gate: {
        title: 'Installe ZERO KAMI',
        message: "Installe l'application sur ton écran d'accueil pour jouer en mode panoramique.",
        button: "Installer l'application",
        installed: "C'est installé ! Ouvre ZERO KAMI depuis ton écran d'accueil pour jouer en mode panoramique.",
        manualGeneric: "Ouvre le menu de ton navigateur, puis choisis « Ajouter à l'écran d'accueil » (ou « Installer l'application »).",
        manualIosOther: "Ouvre cette page dans Safari, puis installe l'application avec le bouton Partager.",
      },
      hint: {
        title: 'Installer le jeu',
        step1Before: 'Appuie sur le bouton',
        step1Bold: 'Partager',
        step1After: '',
        step1Location: ", près de la barre d'adresse de Safari.",
        step2Before: 'Choisis',
        step2Bold: "« Sur l'écran d'accueil »",
        step3Before: 'Confirme avec',
        step3Bold: 'Ajouter',
        footnote: "Une fois installé sur ton écran d'accueil, le jeu s'ouvrira en plein écran, comme une application.",
      },
    },
    stats: { family: 'Famille', remaining: 'Cherche', found: 'Trouvé', score: 'Score' },
    // Score de la partie (#37) : sous le message de fin de niveau, de batterie vide et de fin du jeu (voir game.js#refreshScoreLine)
    score: { soFar: 'Score : {score}', final: 'Score : {score} · Record : {best}', newBest: '🏆 Nouveau record : {score}', best: 'Record : {best}' },
    viewfinder: { shootHint: 'Appuie sur le déclencheur', shutter: 'Déclencheur : prendre la photo', battery: 'Batterie : {left} photos sur {total}', alreadyInAlbum: 'Déjà dans ton album : trouve les nouveaux' },
    // Message d'aide qui alterne au-dessus du viseur après une longue inactivité (voir game.js#refreshHintMessage)
    playHint: {
      // On glisse le décor, qui suit le doigt — donc dans le sens opposé
      dragUp: 'Des kamis en haut : glisse le doigt vers le bas',
      dragDown: 'Des kamis en bas : glisse le doigt vers le haut',
      dragLeft: 'Des kamis à gauche : glisse le doigt vers la droite',
      dragRight: 'Des kamis à droite : glisse le doigt vers la gauche',
    },
    film: { label: 'Pellicule : {count} kamis photographiés' },
    gallery: {
      buttonTitle: 'Galerie',
      title: 'Galerie',
      empty: "Aucun kami photographié pour l'instant. Cadre une lueur dans le viseur et maintiens l'appui !",
      level: 'Niveau {level}',
      backHint: 'Touche le portrait pour revenir',
    },
    share: {
      buttonTitle: 'Partager',
      modal: {
        title: "Partager l'appli",
      },
      // Carte de partage : un kami au hasard, ses attributs et le QR code (ui/shareModal.js, ui/cardPainter.js)
      card: {
        aria: 'Carte de {name} : scanne le QR code pour jouer à ZERO KAMI',
        number: 'N° {n}',
        character: 'Caractère',
        playLead: 'Joue à', // suivi du logo « ZERO KAMI » dessiné sur la carte
        play: 'Joue à ZERO KAMI',
        scan: 'Scanne pour jouer et découvrir les autres kamis',
        share: 'Partager',
        copied: 'Copié dans le presse-papiers',
        copyFailed: 'Copie impossible',
        another: 'Autre carte',
        rewardFast: 'Charge rapide gagnée ⚡',
        rewardLongLife: 'Batterie longue durée gagnée 🔋',
        tourDone: 'Tu as fait le tour de tes cartes ! Photographie d’autres kamis pour en débloquer de nouvelles.',
      },
    },
    music: { toggleTitle: 'Musique', volumeDown: 'Son -', volumeUp: 'Son +' },
    gyro: {
      toggle: 'Réalité virtuelle : viser en bougeant le téléphone',
      denied: "Accès aux capteurs refusé : le glisser reste disponible. Recharge la page puis retouche VR pour redemander l'autorisation",
      lowered: 'Téléphone baissé : retour au mode normal, glisse le décor',
      unavailable: 'Gyroscope indisponible sur cet appareil',
    },
    // Explication au clic sur « Charge rapide » / « Batterie longue durée » quand ce n'est pas encore disponible (ui/chargeInfoDialog.js)
    chargeInfo: {
      fast: "Pour gagner une charge rapide, partage la carte d'un kami que tu n'avais pas encore partagé",
      longLife: 'Pour gagner une batterie longue durée, partage la carte de chaque kami de ta galerie ({shared}/{total})',
      orInstall: ", ou installe l'appli pour jouer sans limite.",
      stop: '.',
      shareFast: 'Recharger en partageant',
      shareLongLife: 'Obtenir en partageant',
      install: "Installer l'appli",
    },
    finale: {
      title: 'Bravo !',
      popup: 'Toute la famille est au complet. Tu gagnes une batterie longue durée !',
      continue: 'Continuer',
    },
    close: { aria: 'Fermer' },
    overlay: {
      button: { start: 'Jouer', win: 'Niveau suivant', survive: 'Niveau suivant', lose: 'Continuer', again: 'Rejouer' },
      finale: 'Bravo, toute la famille est au complet !',
      fastCharge: 'Charge rapide',
      longLife: 'Batterie longue durée',
      longLifeDone: 'Batterie longue durée en service : autonomie et photos doublées',
      fastChargeDone: 'Charge rapide utilisée',
      charging: '🔋 Batterie vide\nEn charge…\nPatiente {time} 🔋',
      charged: '⚡ Batterie chargée ! ⚡',
    },
    dev: {
      title: 'Réglages Dev',
      language: { label: 'Langue', fr: 'Français', en: 'English' },
      groups: { movement: 'Déplacement', gyro: 'Gyroscope', ghosts: 'Kamis', game: 'Partie', battery: 'Batterie', help: 'Aide', sound: 'Son' },
      worldGrowth: { label: 'Agrandissement du monde par niveau', min: '+0', max: '+800' },
      captureRadius: { label: 'Rayon de capture du viseur', min: 'Serré (16)', max: 'Large (80)' },
      panSpeed: { label: "Vitesse de l'objectif au départ", min: 'Lente (40)', max: 'Rapide (400)' },
      panMaxSpeed: { label: "Vitesse de l'objectif appui maintenu", min: '120', max: '1200' },
      panAccelTime: { label: "Temps d'accélération", min: 'Immédiat', max: '4 s' },
      wheelSensitivity: { label: 'Sensibilité trackpad / molette', min: 'x0.3', max: 'x3.0' },
      dragFriction: { label: 'Freinage après un glisser', min: 'Longue glissade (1)', max: 'Arrêt net (12)' },
      gyroHorizonOffset: { label: "Ligne d'horizon (inclinaison du téléphone au centre)", min: 'Plus bas (-40°)', max: 'Plus haut (+40°)' },
      gyroYawSpeed: { label: 'Vitesse de défilement horizontal', min: 'x0.3', max: 'x3.0' },
      gyroPitchSpeed: { label: 'Vitesse de défilement vertical', min: 'x0.3', max: 'x3.0' },
      gyroSmoothing: { label: 'Lissage des mesures', min: 'Brut (0 %)', max: 'Fluide (90 %)' },
      familySize: { label: 'Taille de la famille', min: 'Selon le niveau (5 à 15)', max: 'Toute la famille (15)' },
      photoCost: { label: "Consommation d'une photo (% de la batterie)", min: '1 % (100 photos)', max: '25 % (4 photos)' },
      ghostSpeed: { label: 'Vitesse des fantômes (niv. 1)', min: 'Immobiles', max: 'Rapides (120)' },
      summonDelay: { label: 'Un kami vient me voir après…', min: 'Tout de suite, en permanence (0)', max: "60 s d'inactivité" },
      summonSpeed: { label: 'Vitesse à laquelle il vient me voir', min: 'Lent (20)', max: 'Éclair (1500)' },
      hintDelay: { label: "Délai avant l'aide des coins", min: '1 s', max: '15 s' },
      bpm: { label: 'Tempo Musique (BPM)', min: 'Lent (60)', max: 'Rapide (240)' },
      batteryLevel: { label: 'Charge actuelle de la batterie', min: 'Vide (0 %)', max: 'Pleine (100 %)' },
      batterySeconds: { label: 'Autonomie de la batterie (sans photo)', min: '30 s', max: '30 min' },
      chargeMinutes: { label: 'Durée de la charge (batterie vide)', min: 'Aucune', max: '3 h' },
      startLevel: { label: 'Niveau de Départ', min: 'Niveau 1', max: 'Niveau 6' },
    },
  },
  en: {
    legal: {
      product: 'ZERO KAMI v{version}',
      licensed: 'Published and distributed by 0cog Inc. under exclusive license.',
      trademark: '0cog™ is a trademark of 0cog Inc.',
    },
    about: {
      link: 'About',
      title: 'About',
      contributors: 'Contributors',
      thanks: 'Acknowledgements',
      thanksLead: 'Thanks to the authors of the free software and resources used in ZERO KAMI:',
      sources: 'Sources',
      rights: '© 2026 ZERO KAMI contributors. All rights reserved.',
      privacy: 'Privacy policy',
    },
    meta: {
      lang: 'en',
      description: 'ZERO KAMI — photograph the whole ghost family before you run out of film.',
    },
    app: { subtitle: "The Kamis are here — don't miss them.", level: 'Level {level}' },
    brand: { line: 'a 0cog™ game' },
    settings: {
      title: 'Settings',
      language: 'Language',
      replayTips: 'Show the tips again',
      replayNote: 'Shown when you save',
      reducedMotion: 'Reduce animations',
      iosVersion: 'iOS version (simulation)',
      iosNote: 'Not available yet: one fast charge offered per level',
      resetGame: 'Reset the game',
      resetNote: 'Clears the gallery, the rewards and the level when you save',
      game: 'Game settings',
      tabs: 'Settings categories',
      tabGeneral: 'General',
      gameNote: 'Saved on this device only: other players keep the default settings.',
      resetSettings: '↺ Reset to default settings',
      resetDone: 'Settings reset to default: save to keep them',
      save: 'Save',
      cancel: 'Cancel',
      unsaved: 'Unsaved changes',
      saved: 'Settings saved',
    },
    help: {
      title: 'Tips',
      step: 'Step {n} of {total}',
      swipe: 'Swipe for the next step',
      continue: 'Continue',
      letsGo: "Let's go!",
      s1: { title: 'The kamis are here', text: 'Little spirits are hiding all around you. Find them all and create your own album.', reward: 'At every level, new kamis are waiting for you.' },
      s2: { title: 'Drag to explore', text: 'Drag the scenery with your finger. The frame corners blink on the side where a kami is hiding.', reward: 'On your phone, in VR mode, the kamis are all around you.' },
      s3: { title: 'Frame and shoot', text: 'Put a kami in the circle: the button turns green. Press it.', reward: 'The more kamis you photograph, the closer you get to victory.' },
      s4: { title: 'Watch your battery', text: 'It drains with every photo, and with time. When it is empty the camera charges: you have to wait.', reward: 'Share a kami for a fast charge and get back into the game without waiting.' },
      s5: { title: 'Turn your phone', text: 'Lay your phone on its side for panoramic mode: a big frame to track the kamis.', reward: 'To do so, install the app on your Home Screen first.' },
    },
    splash: { subtitle: "The Kamis are here — don't miss them." },
    install: {
      launchPopup: {
        message: 'Install the game on your Home Screen',
        yes: 'Yes',
        later: 'Later',
      },
      gate: {
        title: 'Install ZERO KAMI',
        message: 'Install the app on your Home Screen to play in panoramic mode.',
        button: 'Install the app',
        installed: 'Installed! Open ZERO KAMI from your Home Screen to play in panoramic mode.',
        manualGeneric: 'Open your browser menu, then choose "Add to Home Screen" (or "Install app").',
        manualIosOther: 'Open this page in Safari, then install the app with the Share button.',
      },
      hint: {
        title: 'Install the game',
        step1Before: 'Tap the',
        step1Bold: 'Share',
        step1After: 'button',
        step1Location: ", near Safari's address bar.",
        step2Before: 'Choose',
        step2Bold: '"Add to Home Screen"',
        step3Before: 'Confirm with',
        step3Bold: 'Add',
        footnote: 'Once installed on your Home Screen, the game will open full-screen, just like an app.',
      },
    },
    stats: { family: 'Family', remaining: 'To find', found: 'Found', score: 'Score' },
    score: { soFar: 'Score: {score}', final: 'Score: {score} · Best: {best}', newBest: '🏆 New best: {score}', best: 'Best: {best}' },
    viewfinder: { shootHint: 'Press the shutter', shutter: 'Shutter: take the picture', battery: 'Battery: {left} shots out of {total}', alreadyInAlbum: 'Already in your album: find the new ones' },
    playHint: {
      dragUp: 'Kamis above: drag your finger down',
      dragDown: 'Kamis below: drag your finger up',
      dragLeft: 'Kamis to the left: drag your finger right',
      dragRight: 'Kamis to the right: drag your finger left',
    },
    film: { label: 'Film strip: {count} kamis photographed' },
    gallery: {
      buttonTitle: 'Gallery',
      title: 'Gallery',
      empty: 'No kami photographed yet. Frame a glow in the viewfinder and hold to shoot!',
      level: 'Level {level}',
      backHint: 'Tap the portrait to go back',
    },
    share: {
      buttonTitle: 'Share',
      modal: {
        title: 'Share the app',
      },
      card: {
        aria: "{name}'s card: scan the QR code to play ZERO KAMI",
        number: 'No. {n}',
        character: 'Character',
        playLead: 'Play', // followed by the "ZERO KAMI" logo drawn on the card
        play: 'Play ZERO KAMI',
        scan: 'Scan to play and meet the other kamis',
        share: 'Share',
        copied: 'Copied to clipboard',
        copyFailed: 'Copy failed',
        another: 'Another card',
        rewardFast: 'Fast charge earned ⚡',
        rewardLongLife: 'Long-life battery earned 🔋',
        tourDone: 'You’ve seen all your cards! Photograph more kamis to unlock new ones.',
      },
    },
    music: { toggleTitle: 'Music', volumeDown: 'Volume -', volumeUp: 'Volume +' },
    gyro: {
      toggle: 'Virtual reality: aim by moving your phone',
      denied: 'Motion sensors blocked: you can still drag. Reload the page, then tap VR again to ask for permission again',
      lowered: 'Phone lowered: back to normal mode, drag the scenery',
      unavailable: 'No gyroscope available on this device',
    },
    chargeInfo: {
      fast: "To earn a fast charge, share the card of a kami you haven't shared before",
      longLife: 'To earn a long-life battery, share the card of every kami in your gallery ({shared}/{total})',
      orInstall: ', or install the app to play without limits.',
      stop: '.',
      shareFast: 'Recharge by sharing',
      shareLongLife: 'Get it by sharing',
      install: 'Install the app',
    },
    finale: {
      title: 'Well done!',
      popup: 'The whole family is together. You win a long-life battery!',
      continue: 'Continue',
    },
    close: { aria: 'Close' },
    overlay: {
      button: { start: 'Play', win: 'Next level', survive: 'Next level', lose: 'Continue', again: 'Play again' },
      finale: 'Well done, the whole family is together!',
      fastCharge: 'Fast charge',
      longLife: 'Long-life battery',
      longLifeDone: 'Long-life battery on: battery life and photos doubled',
      fastChargeDone: 'Fast charge used',
      charging: '🔋 Battery empty\nCharging…\nPlease wait {time} 🔋',
      charged: '⚡ Battery charged! ⚡',
    },
    dev: {
      title: 'Dev Settings',
      language: { label: 'Language', fr: 'Français', en: 'English' },
      groups: { movement: 'Movement', gyro: 'Gyroscope', ghosts: 'Kamis', game: 'Game', battery: 'Battery', help: 'Hints', sound: 'Sound' },
      worldGrowth: { label: 'World growth per level', min: '+0', max: '+800' },
      captureRadius: { label: 'Viewfinder capture radius', min: 'Tight (16)', max: 'Wide (80)' },
      panSpeed: { label: 'Lens speed on press', min: 'Slow (40)', max: 'Fast (400)' },
      panMaxSpeed: { label: 'Lens speed when held', min: '120', max: '1200' },
      panAccelTime: { label: 'Acceleration time', min: 'Instant', max: '4 s' },
      wheelSensitivity: { label: 'Trackpad / wheel sensitivity', min: 'x0.3', max: 'x3.0' },
      dragFriction: { label: 'Braking after a drag', min: 'Long glide (1)', max: 'Hard stop (12)' },
      gyroHorizonOffset: { label: 'Horizon line (phone tilt at the centre)', min: 'Lower (-40°)', max: 'Higher (+40°)' },
      gyroYawSpeed: { label: 'Horizontal scroll speed', min: 'x0.3', max: 'x3.0' },
      gyroPitchSpeed: { label: 'Vertical scroll speed', min: 'x0.3', max: 'x3.0' },
      gyroSmoothing: { label: 'Measurement smoothing', min: 'Raw (0 %)', max: 'Smooth (90 %)' },
      familySize: { label: 'Family size', min: 'By level (5 to 15)', max: 'The whole family (15)' },
      photoCost: { label: 'Cost of a photo (% of the battery)', min: '1% (100 photos)', max: '25% (4 photos)' },
      ghostSpeed: { label: 'Ghost speed (lvl 1)', min: 'Still', max: 'Fast (120)' },
      summonDelay: { label: 'A kami comes to me after…', min: 'Right away, all the time (0)', max: '60 s idle' },
      summonSpeed: { label: 'How fast it comes to me', min: 'Slow (20)', max: 'Lightning (1500)' },
      hintDelay: { label: 'Delay before corner hints', min: '1 s', max: '15 s' },
      bpm: { label: 'Music Tempo (BPM)', min: 'Slow (60)', max: 'Fast (240)' },
      batteryLevel: { label: 'Current battery charge', min: 'Empty (0 %)', max: 'Full (100 %)' },
      batterySeconds: { label: 'Battery life (no photos)', min: '30 s', max: '30 min' },
      chargeMinutes: { label: 'Charging time (empty battery)', min: 'None', max: '3 h' },
      startLevel: { label: 'Start Level', min: 'Level 1', max: 'Level 6' },
    },
  },
});
